import { useProjectContext } from "@contexts/projects";

// Página "Sobre": ficha cadastral da obra ativa, só leitura. Os dados vêm de
// `activeProject.about` (montado no `getDetail` a partir do cadastro em `buildings`).
// A lista é propositalmente ampla — campos sem valor ficam como "—" para facilitar
// decidir depois o que cortar.

const EMPTY = "—";

const ERP_LABELS = Object.freeze({ SIENGE: "Sienge", MEGA: "MEGA" });
const LEVEL_LABELS = Object.freeze({ trial: "Trial" });
const PREVISION_LABELS = Object.freeze({
  live: "Integrado",
  demo: "Demonstração",
});

const formatDate = (value) => {
  if (!value) return EMPTY;
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? EMPTY
    : date.toLocaleDateString("pt-BR", { timeZone: "UTC" });
};

const formatMonth = (value) => {
  if (!value) return EMPTY;
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? EMPTY
    : date.toLocaleDateString("pt-BR", { month: "long", year: "numeric", timeZone: "UTC" });
};

const formatArea = (value) => (
  Number.isFinite(value)
    ? `${value.toLocaleString("pt-BR", { maximumFractionDigits: 2 })} m²`
    : EMPTY
);

const formatCnpj = (value) => {
  const digits = String(value ?? "").replace(/\D/g, "");
  if (digits.length !== 14) return value || EMPTY;
  return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
};

const formatBool = (value) => {
  if (value === true) return "Sim";
  if (value === false) return "Não";
  return EMPTY;
};

const durationInMonths = (start, end) => {
  const startDate = start ? new Date(start) : null;
  const endDate = end ? new Date(end) : null;
  if (!startDate || !endDate || Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return EMPTY;
  }
  const months = (endDate.getUTCFullYear() - startDate.getUTCFullYear()) * 12
    + (endDate.getUTCMonth() - startDate.getUTCMonth());
  return months >= 0 ? `${months} ${months === 1 ? "mês" : "meses"}` : EMPTY;
};

const location = (about) => {
  const parts = [about?.city, about?.state].filter(Boolean);
  return parts.length ? parts.join(" / ") : EMPTY;
};

const indexerLabel = (indexer) => {
  if (!indexer) return EMPTY;
  return [indexer.name, indexer.uf, indexer.standard].filter(Boolean).join(" · ") || EMPTY;
};

const Field = ({ label, value }) => (
  <div className="project-about__field">
    <dt>{label}</dt>
    <dd>{value === null || value === undefined || value === "" ? EMPTY : value}</dd>
  </div>
);

const Section = ({ title, children }) => (
  <section className="project-about__section" aria-label={title}>
    <h2>{title}</h2>
    <dl>{children}</dl>
  </section>
);

const About = () => {
  const { activeProject } = useProjectContext();
  const about = activeProject?.about;

  if (!activeProject) {
    return <p className="project-about__empty">Carregando informações da obra…</p>;
  }

  if (!about) {
    return (
      <p className="project-about__empty">
        As informações cadastrais desta obra ainda não estão disponíveis.
      </p>
    );
  }

  return (
    <div className="project-about">
      <header className="project-about__header">
        <p className="project-about__eyebrow">Sobre a obra</p>
        <h1>{activeProject.custom_name ?? about.building_name ?? activeProject.building_name}</h1>
        {activeProject.custom_name && about.building_name !== activeProject.custom_name ? (
          <p className="project-about__subtitle">Nome no ERP: {about.building_name}</p>
        ) : null}
      </header>

      <div className="project-about__grid">
        <Section title="Identificação">
          <Field label="Nome da obra" value={about.building_name} />
          <Field label="Nome personalizado" value={activeProject.custom_name} />
          <Field label="Código da obra" value={about.building_code} />
          <Field label="Código WBS" value={about.wbs_code_id} />
          <Field label="CNPJ da obra" value={formatCnpj(about.cnpj)} />
          <Field label="Código reduzido (MEGA)" value={about.projeto_reduzido} />
        </Section>

        <Section title="Localização">
          <Field label="Cidade / UF" value={location(about)} />
          <Field label="Cidade" value={about.city} />
          <Field label="Estado (UF)" value={about.state} />
        </Section>

        <Section title="Construtora e integração">
          <Field label="Construtora" value={about.company_name} />
          <Field label="Código da construtora" value={about.company_code} />
          <Field label="ERP" value={ERP_LABELS[about.erp] ?? about.erp} />
          <Field label="Prevision" value={PREVISION_LABELS[about.prevision_mode] ?? about.prevision_mode ?? "Não integrado"} />
          <Field label="Ferramenta de planejamento" value={about.plan_tool} />
        </Section>

        <Section title="Características">
          <Field label="Área" value={formatArea(about.area)} />
          <Field label="Escopo" value={about.scope} />
          <Field label="Nível de apropriação" value={about.appropriation_level} />
          <Field label="Controle de estoque" value={formatBool(about.has_stock)} />
          <Field label="Obra ativa" value={formatBool(about.active)} />
          <Field label="Plano" value={LEVEL_LABELS[about.building_level] ?? about.building_level ?? "Completo"} />
        </Section>

        <Section title="Prazos">
          <Field label="Início" value={formatDate(about.start_date)} />
          <Field label="Término previsto" value={formatDate(about.end_date)} />
          <Field label="Previsão de término (tendência)" value={formatDate(about.prediction_of_the_end)} />
          <Field label="Duração prevista" value={durationInMonths(about.start_date, about.end_date)} />
        </Section>

        <Section title="Orçamento">
          <Field label="Data-base do orçamento" value={formatMonth(about.budget_delivery_date)} />
          <Field label="Versão do orçamento" value={about.budget_version} />
          <Field label="Indexador" value={indexerLabel(about.indexer)} />
        </Section>

        <Section title="Cadastro">
          <Field label="Cadastrada em" value={formatDate(about.created_at)} />
          <Field label="Última alteração do cadastro" value={formatDate(about.updated_at)} />
        </Section>
      </div>
    </div>
  );
};

export default About;
